import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth, ROLE_LABELS } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import StatusBadge, { ActiveBadge } from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const EMPTY = { name: "", email: "", password: "", role: "COMPANY_MANAGER", company_ids: [] };

export default function AccountsPage() {
  const { isAdmin, user: currentUser } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/accounts"), api.get("/companies")]);
    setAccounts(a.data);
    setCompanies(c.data);
  };

  useEffect(() => {
    if (isAdmin) load().catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div data-testid="accounts-page">
        <PageHeader title="Accounts & Permissions" subtitle="User access management" />
        <EmptyState title="Administrator access required" description="Only Administrator / Group Finance can manage accounts." testId="accounts-forbidden" />
      </div>
    );
  }

  const companyName = (id) => companies.find((c) => c.id === id)?.name || id;

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, email: a.email, password: "", role: a.role, company_ids: a.company_ids || [] });
    setDialogOpen(true);
  };

  const toggleCompany = (id) => {
    setForm((f) => ({
      ...f,
      company_ids: f.company_ids.includes(id) ? f.company_ids.filter((c) => c !== id) : [...f.company_ids, id],
    }));
  };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, company_ids: form.company_ids };
        if (form.password) payload.password = form.password;
        await api.patch(`/accounts/${editing.id}`, payload);
        toast.success("Account updated");
      } else {
        await api.post("/accounts", form);
        toast.success("Account created");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/accounts/${a.id}`, { active: !a.active });
      toast.success(a.active ? "Account deactivated" : "Account activated");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="accounts-page">
      <PageHeader
        title="Accounts & Permissions"
        subtitle="User accounts, roles and company access"
        actions={
          <Button onClick={openCreate} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="new-account-button">
            <Plus className="h-4 w-4 mr-1.5" /> New account
          </Button>
        }
      />
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="accounts-table">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Account</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Role</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Company access</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
              <th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-accent/40 transition-colors" data-testid={`account-row-${a.email}`}>
                <td className="py-3 px-4">
                  <span className="font-semibold text-slate-800 text-sm">{a.name}</span>
                  <span className="text-xs text-slate-500 block">{a.email}</span>
                </td>
                <td className="py-3 px-4">
                  <StatusBadge tone={a.role === "ADMINISTRATOR" ? "blue" : "slate"} testId={`account-role-${a.id}`}>
                    {ROLE_LABELS[a.role] || a.role}
                  </StatusBadge>
                </td>
                <td className="py-3 px-4 text-sm text-slate-700">
                  {a.role === "ADMINISTRATOR"
                    ? "All companies"
                    : (a.company_ids || []).length > 0
                      ? a.company_ids.map(companyName).join(", ")
                      : "None"}
                </td>
                <td className="py-3 px-4"><ActiveBadge active={a.active} testId={`account-status-${a.id}`} /></td>
                <td className="py-3 px-4 text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(a)} data-testid={`edit-account-${a.id}`}>Edit</Button>
                    {a.id !== currentUser?.id && (
                      <Button variant="outline" size="sm" onClick={() => toggleActive(a)} data-testid={`toggle-account-${a.id}`}>
                        {a.active ? "Deactivate" : "Activate"}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {accounts.length === 0 && <EmptyState title="No accounts" testId="accounts-empty" />}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="account-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit account" : "New account"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="account-email-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{editing ? "New password (optional)" : "Password"}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="account-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="account-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMINISTRATOR" data-testid="role-option-administrator">Administrator</SelectItem>
                    <SelectItem value="COMPANY_MANAGER" data-testid="role-option-company-manager">Company Manager</SelectItem>
                    <SelectItem value="READ_ONLY_MANAGEMENT" data-testid="role-option-read-only">Read-only Management</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.role !== "ADMINISTRATOR" && (
              <div className="space-y-2">
                <Label>Permitted companies</Label>
                <div className="space-y-2 rounded-md border border-slate-200 p-3">
                  {companies.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <Checkbox
                        checked={form.company_ids.includes(c.id)}
                        onCheckedChange={() => toggleCompany(c.id)}
                        data-testid={`account-company-checkbox-${c.id}`}
                      />
                      {c.name}
                    </label>
                  ))}
                  {companies.length === 0 && <span className="text-xs text-slate-500">No companies available</span>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="account-cancel-button">Cancel</Button>
            <Button onClick={save} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="account-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
